<script setup>
import {
  SearchOutlined,
  ReloadOutlined,
  PlusOutlined,
} from "@ant-design/icons-vue";
import { message } from "ant-design-vue";
import { h, ref, unref, reactive, onMounted } from "vue";
import { deleteRequest, getRequest } from "@/utils/request.js";
import RoleFormModal from "./modules/RoleFormModal.vue";
import RoleGrantPermissionModal from "./modules/RoleGrantPermissionModal.vue";

const formRef = ref();
const searchFormModel = ref({});
const datasource = ref([]);
const loading = ref(false);
const modalRef = ref();
const grantModalRef = ref();
const columns = [
  {
    key: "name",
    dataIndex: "name",
    title: "角色名称",
    width: "30%",
  },
  {
    key: "code",
    dataIndex: "code",
    title: "角色编号",
    width: "30%",
  },
  {
    key: "description",
    dataIndex: "description",
    title: "角色说明",
    width: "30%",
  },
  {
    key: "action",
    dataIndex: "action",
    title: "操作",
    width: "300px",
  },
];

const pagination = reactive({
  current: 1,
  pageSize: 10,
  pageSizeOptions: ["10", "50", "100"],
  showTotal: (total, range) => {
    return range[0] + "-" + range[1] + " 共" + total + "条";
  },
  showQuickJumper: true,
  showSizeChanger: true,
  total: 0,
});

const loadData = () => {
  loading.value = true;
  const { current, pageSize } = unref(pagination);
  const requestBody = Object.assign({}, unref(searchFormModel), {
    pageNo: current,
    pageSize,
  });
  getRequest("/role", requestBody)
    .then((response) => {
      if (response.success) {
        pagination.total = response.data.total;
        datasource.value = response.data.records;
      }
    })
    .finally(() => {
      loading.value = false;
    });
};

const resetForm = () => {
  formRef.value.resetFields();
  loadData();
};

const handleAdd = () => {
  modalRef.value.title = "用户新增";
  modalRef.value.handleAdd();
};

const handleEdit = (record) => {
  modalRef.value.title = "用户编辑";
  modalRef.value.handleEdit(record);
};

const handleDelete = (record) => {
  deleteRequest(`/role/${record.id}`).then((response) => {
    if (response.success) {
      message.success("删除成功");
      loadData();
    } else {
      message.error("删除失败。" + response.message);
    }
  });
};

const handleGrant = (record) => {
  grantModalRef.value.handleOpen(record);
};

const tableChange = ({ current, pageSize }) => {
  pagination.current = current;
  pagination.pageSize = pageSize;
  loadData();
};

onMounted(() => {
  loadData();
});
</script>

<template>
  <div>
    <a-card>
      <a-form
        ref="formRef"
        layout="inline"
        :labelCol="{ span: 3, offset: 1 }"
        :model="searchFormModel"
        @keydown.enter="loadData"
      >
        <a-row :gutter="[10, 10]" style="width: 100%">
          <a-col :span="6">
            <a-form-item label="角色名称" name="name">
              <a-input
                v-model:value="searchFormModel.name"
                placeholder="请输入角色名称"
              />
            </a-form-item>
          </a-col>
          <a-col :span="6">
            <a-form-item label="角色编号" name="code">
              <a-input
                v-model:value="searchFormModel.code"
                placeholder="请输入角色编号"
              />
            </a-form-item>
          </a-col>
          <a-col :span="24">
            <a-button
              type="primary"
              style="margin-left: 18px"
              :icon="h(SearchOutlined)"
              @click="loadData"
              @keydown.enter="loadData"
            >
              查询
            </a-button>
            <a-button
              style="margin-left: 18px"
              :icon="h(ReloadOutlined)"
              @click="resetForm"
              >重置
            </a-button>
          </a-col>
        </a-row>
      </a-form>
    </a-card>

    <a-card style="margin-top: 10px">
      <div style="margin-bottom: 12px">
        <a-button
          v-has="'role:add'"
          type="primary"
          @click="handleAdd"
          :icon="h(PlusOutlined)"
          >新增
        </a-button>
      </div>
      <a-table
        row-key="id"
        :loading="loading"
        :columns="columns"
        :data-source="datasource"
        :bordered="true"
        :pagination="pagination"
        @change="tableChange"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.dataIndex === 'status'">
            <template v-if="record[column.dataIndex]">
              <a-tag color="green">启用</a-tag>
            </template>
            <template v-else>
              <a-tag color="red">停用</a-tag>
            </template>
          </template>

          <template v-if="column.dataIndex === 'action'">
            <a v-has="'role:update'" type="link" @click="handleEdit(record)"
              >编辑</a
            >
            <a-divider type="vertical" />
            <a-popconfirm title="确认要删除吗?" @confirm="handleDelete(record)">
              <a v-has="'role:delete'" type="link">删除</a>
            </a-popconfirm>
            <a-divider type="vertical" />
            <a v-has="'role:grant'" type="link" @click="handleGrant(record)"
              >授权</a
            >
          </template>
        </template>
      </a-table>
    </a-card>
    <role-form-modal ref="modalRef" @ok="loadData" />
    <role-grant-permission-modal ref="grantModalRef" />
  </div>
</template>

<style scoped lang="scss"></style>
